import { FilterBlocksEnum, IFilterGridItem } from '@/interfaces/constructor.interfaces';
import { FilterRawValuesType } from '@/interfaces/filters.interfaces';
import instanceOfReference from '@/utils/instanceOfReference';
import isIsoDate from '@/utils/isIsoDate';

import splitCompositeFilterPath from './splitCompositeFilterPath';

export const getFilterQuery = (
  filterGridItem: IFilterGridItem,
  filterRawValues?: FilterRawValuesType,
  filterValue?: unknown,
) => {
  if (!filterRawValues) {
    return null;
  }

  const { path } = splitCompositeFilterPath(filterGridItem.path);

  switch (filterGridItem.type) {
    case FilterBlocksEnum.FilterLocation:
    case FilterBlocksEnum.FilterMultipleSelect:
    case FilterBlocksEnum.FilterCheckboxGroup:
    case FilterBlocksEnum.FilterModelCheckboxGroup:
    case FilterBlocksEnum.FilterColorPicker:
    case FilterBlocksEnum.FilterDuoColorPicker: {
      const resolvedValue = !Array.isArray(filterValue) ? [filterValue] : filterValue;
      const resolvedRawValues = !Array.isArray(filterRawValues)
        ? [filterRawValues]
        : filterRawValues;

      if (!resolvedValue.length && !resolvedRawValues.length) {
        return null;
      }

      if (instanceOfReference(resolvedRawValues[0])) {
        return `${path}.id in ['${resolvedValue.join(`', '`)}']`;
      }

      if (isIsoDate(resolvedRawValues[0])) {
        return `${path} in [Time.Time('${resolvedValue.join(`'), Time.Time('`)}')]`;
      }

      return `${path} in [${resolvedValue
        .map(v => (typeof v === 'string' ? `'${v}'` : v))
        .join(', ')}]`;
    }
    case FilterBlocksEnum.FilterSelect: {
      const resolvedRawValues = !Array.isArray(filterRawValues)
        ? [filterRawValues]
        : filterRawValues;

      if (!resolvedRawValues.length) {
        return null;
      }

      if (instanceOfReference(resolvedRawValues[0])) {
        return `${path}.id in ['${filterValue}']`;
      }

      if (isIsoDate(resolvedRawValues[0])) {
        return `${path} in [Time.Time('${filterValue}')]`;
      }

      return `${path} in [${
        typeof filterValue === 'string' ? `'${filterValue}'` : filterValue
      }]`;
    }
    case FilterBlocksEnum.FilterSlider: {
      const resolvedRawValues = !Array.isArray(filterRawValues)
        ? [filterRawValues]
        : filterRawValues;

      if (instanceOfReference(resolvedRawValues[0]) || !resolvedRawValues.length) {
        return null;
      }

      if (isIsoDate(resolvedRawValues[0])) {
        return `${path} in [Time.Time('${filterValue}-01-01T00:00:00Z')]`;
      }

      return `${path} in [${
        typeof filterValue === 'string' ? `'${filterValue}'` : filterValue
      }]`;
    }
    case FilterBlocksEnum.FilterRangeSlider: {
      const resolvedValue = !Array.isArray(filterValue) ? [filterValue] : filterValue;

      const resolvedRawValues = !Array.isArray(filterRawValues)
        ? [filterRawValues]
        : filterRawValues;

      if (resolvedValue.length !== 2 && !resolvedRawValues.length) {
        return null;
      }

      if (isIsoDate(resolvedRawValues[0])) {
        return `${path} >= Time.Time('${resolvedValue[0]}-01-01T00:00:00Z') && ${path} <= Time.Time('${resolvedValue[1]}-01-01T00:00:00Z')`;
      }

      return `${path} >= ${resolvedValue[0]} && ${path} <= ${resolvedValue[1]}`;
    }
    default:
      return null;
  }
};

const prepareFilterQuery = (
  filterGridItem: IFilterGridItem,
  filterRawValues?: FilterRawValuesType,
  filterValue?: unknown,
) => {
  if (
    typeof filterValue === 'undefined' ||
    (Array.isArray(filterValue) && !filterValue.length)
  ) {
    return null;
  }
  const filterQuery = getFilterQuery(filterGridItem, filterRawValues, filterValue);
  if (!filterQuery) {
    return null;
  }

  return filterQuery;
};

export default prepareFilterQuery;
